import { DelwinNameAndDate } from '@app/components/various/DelwinNameAndDate';
import { DatePickerField, FieldLabel, FormGrid } from '@app/components/various/Form';
import { getDeliveryDateRange } from '@app/logic/deliveryWindows';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Checkout, CheckoutForm, DeliveryWindow, Order } from '@typings';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CheckoutShippingMethods } from '../CheckoutShippingMethods';

interface Props {
  checkoutFields: Checkout.Requirements;
  deliveryWindows: DeliveryWindow.Mixed[];
  orderId: string;
  splitId: string;
  shippingMethods: Order.ShippingMethod[];
  selectedShippingMethods: Order.SelectedShippingMethod[];
  orderIndex?: number;
}

export const CheckoutShippingPreferencesByOrder = ({
  checkoutFields,
  deliveryWindows,
  orderId,
  splitId,
  orderIndex,
  shippingMethods,
  selectedShippingMethods,
}: Props) => {
  const { t } = useTranslation(['checkout', 'orders', 'shipping']);

  const { watch } = useFormContext<CheckoutForm>();
  const shippingDatesPreferences = watch('shippingDatesPreferences');
  const [shippingDateMin, shippingEndDate] = React.useMemo(() => getDeliveryDateRange(deliveryWindows), [deliveryWindows]);

  const cancelDateMin = shippingDatesPreferences?.[splitId]?.preferredShippingDate?.add(1, 'day') ?? shippingDateMin;
  const shippingDateMax = shippingDatesPreferences?.[splitId]?.cancelDate?.subtract(1, 'day') ?? shippingEndDate;

  const hasPreferredShippingDate = isDefined(checkoutFields.preferredShippingDateBySplitOrder);
  const hasCancelShippingDate = isDefined(checkoutFields.cancelDateBySplitOrder);
  const hasAnyDate = hasPreferredShippingDate || hasCancelShippingDate;

  const isPreferredShippingDateRequired = !!checkoutFields.preferredShippingDateBySplitOrder?.required;
  const isCancelDateRequired = !!checkoutFields.cancelDateBySplitOrder?.required;

  if (!hasAnyDate && isEmpty(shippingMethods)) {
    return null;
  }

  return (
    <div className="box">
      <h3 className="heading-3">
        {t('checkout:shipping_preferences')}
        {isDefined(orderIndex) ? ` - ${t('orders:order_index', { index: orderIndex })}` : ''}
      </h3>
      <div className="relative z-interactiveTableItem -ml-1 mt-1 flex flex-wrap">
        {deliveryWindows.map(deliveryWindow => (
          <DelwinNameAndDate key={deliveryWindow.deliveryWindow} deliveryWindow={deliveryWindow} insertCommas>
            <span className="body-2">{deliveryWindow.name}</span>
          </DelwinNameAndDate>
        ))}
      </div>
      <p className="mt-5 text-neutral-40 body-2">{t('checkout:shipping_preferences_description')}</p>
      <div className="mt-5 flex flex-col gap-8">
        {hasAnyDate && (
          <FormGrid cols={hasPreferredShippingDate && hasCancelShippingDate ? 2 : 1}>
            {hasPreferredShippingDate && (
              <DatePickerField<CheckoutForm>
                name={`shippingDatesPreferences.${splitId}.preferredShippingDate`}
                label={t('shipping:pref_shipping_date')}
                isRequired={isPreferredShippingDateRequired}
                dataTestId="shipping-date-picker"
                min={shippingDateMin}
                max={shippingDateMax}
              />
            )}

            {hasCancelShippingDate && (
              <DatePickerField<CheckoutForm>
                name={`shippingDatesPreferences.${splitId}.cancelDate`}
                label={t('checkout:cancel_date')}
                isRequired={isCancelDateRequired}
                dataTestId="cancel-date-picker"
                min={cancelDateMin}
              />
            )}
          </FormGrid>
        )}
        {!isEmpty(shippingMethods) && (
          <div className="flex flex-col gap-3">
            <FieldLabel as="div" label={t('shipping:shipping_method')} />
            <CheckoutShippingMethods
              orderId={orderId}
              splitId={splitId}
              methods={shippingMethods}
              selectedMethods={selectedShippingMethods}
            />
          </div>
        )}
      </div>
    </div>
  );
};
